"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Page, UserSummary } from "@theta/api-client";
import { thetaApi, pageOf } from "@/lib/api/theta";
import { useDebounced } from "./useDebounced";

/**
 * @-MENTION SUGGESTIONS — `GET /users?inGroup={ref}`
 *
 * THREE THINGS WENT AWAY HERE.
 *
 * 1. `username`. O-21/O-22 removed it from the wire, so a candidate is an ID
 *    and a DISPLAY NAME and nothing else. The ranking below no longer has a
 *    handle to fall back on and the mention that gets written is `@[id]`, not
 *    `@handle` — which is also the only form that cannot resolve to the wrong
 *    person (ten real accounts have a numeric username equal to a different
 *    user's id).
 * 2. The bespoke `complete` flag on `/theta/users/mention_candidates`, and its
 *    undocumented 300-row cutoff. `page.hasMore === false` IS the "this is the
 *    whole list, filter it locally" signal (§2.2 #121) — the same field every
 *    other collection carries, so there is nothing group-specific to keep in
 *    step.
 * 3. The hand-rolled `setTimeout` + `AbortController` + "is this answer still
 *    for the term I am showing?" key. `useDebounced` and react-query's own
 *    keying do both, and the stale-answer race the old comment described is
 *    structurally gone: an answer is stored under the term it answered.
 *
 * SCOPE IS REQUIRED (§1.4). Without a group there is no unscoped people list
 * to search — that is `GET /users`'s `400 scopeRequired` — so a composer with
 * no group context offers no suggestions rather than asking a question the
 * server refuses.
 */

const MAX_SUGGESTIONS = 8;
const EMPTY: UserSummary[] = [];

/** Rank a preloaded member list the way a person expects their own typing to rank. */
function filterLocally(users: UserSummary[], term: string): UserSummary[] {
  const q = term.toLowerCase();
  const scored: { user: UserSummary; rank: number }[] = [];
  for (const user of users) {
    const name = (user.name ?? "").toLowerCase();
    if (!name) continue;
    let rank: number;
    if (name.startsWith(q)) rank = 0;
    else if (name.split(/\s+/).some((word) => word.startsWith(q))) rank = 1;
    else if (name.includes(q)) rank = 2;
    else continue;
    scored.push({ user, rank });
  }
  scored.sort((a, b) => a.rank - b.rank);
  return scored.slice(0, MAX_SUGGESTIONS).map((s) => s.user);
}

function searchUsers(inGroup: string, q?: string): Promise<Page<UserSummary>> {
  return thetaApi.http
    .GET("/users", {
      params: { query: { inGroup, ...(q ? { q } : {}), limit: 100 } },
    })
    .then(pageOf<UserSummary>);
}

/**
 * @param active whether the composer this belongs to has been touched at all.
 *   The member list is fetched EAGERLY once it is true — before the author
 *   types `@`, so the dropdown has no round trip in it — which is why the flag
 *   has to come from outside: on a feed page every card mounts a composer, and
 *   fetching for all of them on mount cost one request per group on a page
 *   nobody had clicked into. `RichTextInput`'s `engaged` is the caller-side
 *   half of this and carries the measurement.
 */
export function useUserSearch(
  term: string,
  groupRef?: string | number,
  active = true,
): UserSummary[] {
  const ref = groupRef === undefined ? "" : String(groupRef);

  // The group's whole member list, when it fits on one page. `hasMore` is the
  // contract's own answer to "may I filter this locally".
  const { data: candidates } = useQuery({
    queryKey: ["v1", "mention-candidates", ref],
    queryFn: () => searchUsers(ref),
    enabled: active && ref.length > 0,
    staleTime: 5 * 60 * 1000,
  });
  const preloaded = candidates && !candidates.hasMore ? candidates.items : null;

  const localResults = useMemo(
    () => (preloaded && term.length >= 1 ? filterLocally(preloaded, term) : EMPTY),
    [preloaded, term],
  );

  const debounced = useDebounced(term, 200);
  const { data: remote } = useQuery({
    queryKey: ["v1", "user-search", ref, debounced],
    queryFn: () => searchUsers(ref, debounced),
    // A big group only: a preloaded one never reaches the network again.
    enabled: active && ref.length > 0 && !preloaded && debounced.length >= 1,
    staleTime: 60 * 1000,
  });

  if (preloaded) return localResults;
  if (term.length < 1 || debounced !== term) return EMPTY;
  return remote?.items.slice(0, MAX_SUGGESTIONS) ?? EMPTY;
}
